import type { RepeatOptionType } from '@/components/Note/components/TodoModal/util';
import type { CanvasDetailResponse, CanvasUrlType } from '@/types/canvas';
import type { PagingResponse } from '@/types/common';
import type { CommonResponse } from '@/types/responseModel';
import { outHost } from '@/utils/host';
import { Request } from '@/utils/request';

import type { EOutputMode, IMemoryId } from './agent';
import type { IFinishGuide } from './guide';
import type { SerendipityItem } from './serendipity';
import type { ISmartTopicBasic } from './smart-topic';

export enum NoteType {
  Quick = 'QUICKNOTE',
  Important = 'Important',
  Normal = 'NOTE'
}

export enum EMemoryType {
  TODO = 'TODO', // 不会使用，todo判断采用的是list接口的另一个字段
  AUDIO = 'AUDIO',
  LINK = 'LINK',
  DOC = 'DOC',
  ANSWER = 'ANSWER',
  IMAGE = 'IMAGE',
  TEXT = 'TEXT',
  SHORT_AUDIO = 'SHORT_AUDIO',
  PAGE = 'PAGE',
  IMPORT = 'IMPORT'
}

export const ReportMemoryTypeMap = {
  [EMemoryType.TEXT]: 'TEXT',
  [EMemoryType.AUDIO]: 'VOICE',
  [EMemoryType.LINK]: 'LINK',
  [EMemoryType.DOC]: 'FILE',
  [EMemoryType.IMAGE]: 'IMAGE',
  [EMemoryType.IMPORT]: 'IMPORT'
};

export enum NoteDataType {
  WEBSITE = 'WEBSITE',
  DOCUMENT = 'DOCUMENT',
  IMAGE = 'IMAGE',
  TABLE = 'TABLE',
  AUDIO = 'AUDIO',
  SHORT_AUDIO = 'SHORT_AUDIO'
}
export enum MemoryDataType {
  Todo = 'Todo',
  Doc = 'Doc',
  Audio = 'Audio',
  Link_Doc = 'Link&Doc',
  Answer = 'Answer',
  Image = 'Image',
  Memo = 'Memo',
  Link = 'Link',
  Page = 'Page',
  Chat = 'Chat'
}

export type ValidMemoryDataType = Exclude<MemoryDataType, MemoryDataType.Link | MemoryDataType.Doc>;

export interface OgiMetadata {
  type?: CanvasUrlType;
  title?: string;
  url?: string;
  image?: string;
  icon?: string;
  siteName?: string;
  description?: string;
}

export interface AudioSegment {
  id: number;
  metaDataAudioId: number;
  segmentStartTime: number; // 秒
  segmentEndTime: number; // 秒
  segmentContent: string;
  createTimestamp?: string;
  updateTimestamp?: string;
}

export interface MetadataAudio {
  id: number;
  docId: number;
  title: string;
  summary: string;
  duration: number; // 秒
  language: string;
  link: string;
  createTimestamp: string;
  updateTimestamp: string;
  segmentList: AudioSegment[];
}

export interface Metadata {
  ogi?: OgiMetadata;
  audio?: MetadataAudio;
}

export interface INoteAttachment extends INote {
  process?: PROCESS_ENUM;
  url?: string;
  title?: string;
  description?: string;
  image?: string;
  icon?: string;
  dataType?: NoteDataType;
  metadata?: Metadata;
  errorMessage?: string;
  createTime?: string;
  id?: number;
  parseFailInfo?: IParseFailInfo;
}
export interface IParseFailInfo {
  level?: ERROR_ENUM;
  message?: string;
  subCode?: string;
}
export enum ERROR_ENUM {
  ERROR = 'ERROR',
  WARNING = 'WARN',
  INFO = 'INFO'
}

export enum PROCESS_ENUM {
  doing = 'doing',
  done = 'done',
  undone = 'undone'
}
export interface INote {
  noteId?: string;
  content?: string;
  createTimestamp?: string; //兼容
  createTime?: string;
  title?: string;
  type?: NoteType;
  memoryType?: EMemoryType;
  attachments?: INoteAttachment[];
  generatedCtd?: IGeneratedCTD;
}

export interface INote_List extends INote {
  mindId: string;
  source: string;
  insight: string;
  summary: string;
  process: PROCESS_ENUM;
  parseFailInfo?: IParseFailInfo;
  smartTopics: ISmartTopicBasic[];
  shareStatus: boolean;
  shareCode?: string;
  errorMessage?: string;
  shareNeedPwdStatus?: boolean;
  sharePublicStatus?: boolean;
  sharePwd?: string;
  loading?: boolean;
  chatCount?: number;
  chatSessionId?: string;
  hasResonance?: boolean;
  stageList?: string[]; //INSIGHT, SUMMARY, SMART_TOPIC
  relatedNoteIds?: string[];
}

export interface ITopicRecommend {
  topic: string;
  relationNoteIds?: string[];
  greetingText?: string; // deprecated
  materialId: string; // deprecated
  dialog: string; // deprecated
  tips: string; // deprecated
  // 前端自行增加的字段
  emoji?: string;
}

export interface IHighlightMap {
  content?: string[];
  title?: string[];
  insight?: string[];
  'attachments.title'?: string[];
  'attachments.content'?: string[];
  'ogi.title'?: string[];
  tldr?: string[];
  snippet?: string[];
}

export interface ISearchNoteReq {
  keyword?: string;
  pageNo?: number;
  pageSize?: number;
  limitNoteIds?: string[];
}

export interface ISearchMemoryReq {
  keyword?: string;
  pageNo?: number;
  pageSize?: number;
  memoryIds?: IMemoryId[];
}

interface ISearchNoteRes {
  list: INote_List[];
  total: number;
}

interface IOperateNoteReq {
  noteId: string;
  mindId: string;
}

export interface IAddNoteReq {
  content?: string;
  title?: string;
  memoryType?: EMemoryType;
  urls?: string[];
  messageIds?: string[];
  audioLanguage?: string;
}

interface IAddVoiceNoteReq {
  mindId: string;
  content?: string;
  title?: string;
  cdnUrl: string;
  language: string;
}

interface IParseParam {
  parseId: string;
  urls: string[];
  mindId: string;
  docId: string;
}

export interface IUpdateNoteReq {
  docId: string;
  title: string;
  content?: string;
  urls?: string[];
  mindId: string;
}

export function searchNoteList(data: ISearchNoteReq) {
  return Request<CommonResponse<ISearchNoteRes>>({
    url: '/rest/os/note/list',
    method: 'post',
    data: {
      keyword: data.keyword || '',
      pageNo: data.pageNo || 1,
      pageSize: data.pageSize || 99999,
      limitNoteIds: data.limitNoteIds || undefined
    }
  });
}

export function searchMemoryList(data: ISearchMemoryReq) {
  return Request<CommonResponse<IFetchMemoryListRes>>({
    url: 'rest/os/note/memory/list/by/ids',
    method: 'post',
    data: {
      keyword: data.keyword || '',
      pageNo: data.pageNo || 1,
      pageSize: data.pageSize || 99999,
      memoryIds: data.memoryIds || undefined
    }
  });
}

export function getNoteDetail(noteId: string) {
  return Request<CommonResponse<INote_List>>({
    url: '/rest/os/note/detail',
    method: 'get',
    params: {
      noteId
    }
  });
}

export function deleteNote(data: IOperateNoteReq) {
  return Request<CommonResponse<ISearchNoteRes>>({
    url: '/rest/os/note/delete',
    method: 'post',
    data
  });
}

// deprecated
export function enhanceNote(data: IOperateNoteReq) {
  return Request<CommonResponse<ISearchNoteRes>>({
    url: '/rest/os/note/enhance',
    method: 'post',
    data
  });
}

// deprecated
export function unenhanceNote(data: IOperateNoteReq) {
  return Request<CommonResponse<ISearchNoteRes>>({
    url: '/rest/os/note/enhance/close',
    method: 'post',
    data
  });
}

export function updateNoteAccess(data: IOperateNoteReq & { open: boolean; openPublic: boolean }) {
  return Request<CommonResponse<ISearchNoteRes>>({
    url: '/rest/os/note/share/put',
    method: 'post',
    data
  });
}

export function getShareNoteDetail(params: { noteId: string }) {
  return Request<CanvasDetailResponse>({
    url: '/rest/os/note/share/by/docId',
    method: 'get',
    params
  });
}

export function getSharedNoteDetailByToken(params: { token: string; password?: string }) {
  return Request<CanvasDetailResponse>({
    url: `/rest/os/note/share/${params.token}`,
    method: 'post',
    data: params
  });
}

export function generateShareToken(data: { noteId: string; mindId: string }) {
  return Request<CommonResponse<string>>({
    url: '/rest/os/note/share/generate/token',
    method: 'post',
    data
  });
}

export function checkNoteShare(params: { noteId: string }) {
  return Request<CommonResponse<boolean>>({
    baseURL: outHost(),
    url: '/rest/out/note/share/check',
    method: 'get',
    params
  });
}

export function addNote(data: IAddNoteReq) {
  return Request<CommonResponse<string>>({
    url: '/rest/os/note/add',
    method: 'post',
    data
  });
}

export function addImageNote(data: IAddNoteReq) {
  return Request<CommonResponse<string>>({
    url: 'rest/os/note/image/add',
    method: 'post',
    data
  });
}

export function addVoiceNote(data: IAddVoiceNoteReq) {
  return Request<CommonResponse<string>>({
    url: '/rest/os/note/audio/add/v2',
    method: 'post',
    data
  });
}

export function parseImage(data: IParseParam) {
  return Request<CommonResponse<string>>({
    url: '/rest/os/note/image/parse/image',
    method: 'post',
    data
  });
}

export function updateImageNote(data: IUpdateNoteReq) {
  return Request<CommonResponse<string>>({
    url: '/rest/os/note/image/update',
    method: 'post',
    data
  });
}

export function getSuggestion() {
  return Request<CommonResponse<ITopicRecommend[]>>({
    url: '/rest/os/note/get/suggestion/by/memory',
    method: 'get'
  });
}

export interface GetTopicSuggestionResult {
  suggestions: ITopicRecommend[];
  relatedNoteIds: number[];
  greetingText?: string;
  opening?: string;
}

export interface IGetNoteSuggestionResult {
  relatedNotesForUser: number[];
}

export function getSuggestionByNoteId(data: {
  noteId?: string;
  todoId?: string;
  refreshSuggest?: boolean;
}) {
  const { noteId, refreshSuggest = false, todoId } = data;

  return Request<CommonResponse<GetTopicSuggestionResult>>({
    url: '/rest/os/note/get/suggestion/by/noteId/v3',
    method: 'get',
    params: { noteId, refreshSuggest, todoId }
  });
}

export function getSimilarNote(data: {
  noteId?: string;
  todoId?: string;
  refreshSuggest?: boolean;
}) {
  const { noteId, refreshSuggest = false, todoId } = data;

  return Request<CommonResponse<IGetNoteSuggestionResult>>({
    url: '/rest/os/note/get/similarNote',
    method: 'get',
    params: { noteId, refreshSuggest, todoId }
  });
}

export function getBindChat(data: {
  noteId?: string;
  todoId?: string;
  pageNo?: number;
  pageSize?: number;
}) {
  const { noteId, todoId, pageNo = 1, pageSize = 999 } = data;

  return Request<CommonResponse<{ list: MemoryListChatItem[]; total: number }>>({
    url: '/rest/os/note/get/bindChat',
    method: 'get',
    params: { noteId, todoId, pageNo, pageSize }
  });
}

export function getChatNoteDetail(data: { sessionId: string }) {
  return Request<CommonResponse<IChatListItem>>({
    url: '/rest/general/session/get/detail',
    method: 'post',
    data
  });
}

export function exposureSuggestion(materialId: string) {
  return Request<CommonResponse<any>>({
    url: '/rest/os/note/get/suggestion/by/memory',
    method: 'get',
    params: { materialId }
  });
}

export function relearnNote(data: IOperateNoteReq) {
  return Request<CommonResponse<any>>({
    url: '/rest/os/note/relearn',
    method: 'post',
    data
  });
}

export type MemoryListUserItem = MemoryListNoteItem | MemoryListTodoItem | MemoryListChatItem;
export type MemoryListMindItem =
  | MemoryListSerendipityItem
  | MemoryListSerendipityList
  | MemoryListUserLevelItem;
export type MemoryListFeaturedItem = MemoryListTalkItem | MemoryListSnapItem;
export type MemoryListItem = MemoryListUserItem | MemoryListMindItem | MemoryListFeaturedItem;

export interface IFetchMemoryListV2Req {
  actor?: 'all' | 'mind' | 'user';
  query?: string;
  smartFolderId?: number;
  dataType?: MemoryDataType;
  sourceList?: EIntegrationType[];
  startTime?: number;
  endTime?: number;
  from: number;
  size?: number;
}

export type MemoryListItemType = 'note' | 'todo' | 'chat';

export type MemoryListMindItemType = 'serendipity' | 'levelUp' | 'serendipityList';

export type MemoryListFeaturedItemType = 'talk' | 'snap';

export interface IFetchMemoryListRes extends PagingResponse<MemoryListItem> {
  analyzeText?: string[];
}

export interface MemoryListItemBase {
  sortTimestamp: number | string;
  id?: number;
  unread?: boolean;
  type: MemoryListItemType | MemoryListMindItemType | MemoryListFeaturedItemType;
  highlightMap?: IHighlightMap;
  snippetList?: string[];
}

export interface ISearchListItem {
  id: number;
  tldr: string;
  searchPic: string;
  title: string;
  shareOpen: boolean;
  status: PROCESS_ENUM;
  createTime: string;
  updateTime: string;
  smartTopics?: ISmartTopicBasic[];
  errorMessage?: string;
}

export interface ISearchDetail extends ISearchListItem {
  insight: string;
  source: string;
  url: string;
  duration: number;
  language: string;
  searchSources?: {
    title?: string;
    url: string;
    icon?: string;
  }[];
}

export interface IChatListItem {
  id: string;
  sessionId: string;
  title: string;
  content?: string;
  summary?: string;
  createTime: number;
  updateTime: number;
  images?: string[];
  hasResonance?: boolean;
  latestMessage?: string;
}

export enum ETodoStatus {
  done = 'done',
  undone = 'undone'
}

export interface ITodayTodoList {
  total: number;
  list: MemoryListTodoItem[];
}

export interface ITodoListItem {
  id: number;
  content: string;
  deadline?: number | '';
  sessionId?: string;
  status: ETodoStatus;
  createTime: number;
  updateTime: number;
  detail?: string;
  reminderTime?: number | '';
  source?: string;
  url?: string; // 短语音的地址
  duration?: number;
  language?: string;
  chatCount?: number;
  repeatType?: RepeatOptionType;
  endReminderDate?: number | ''; // 终止重复的日期, 为空则永不终止
  generatedCtd?: IGeneratedCTD;
}

export interface MemoryListNoteItem extends MemoryListItemBase {
  actor: 'user';
  type: 'note';
  id: number;
  note: INote_List;
}

export interface MemoryListTodoItem extends MemoryListItemBase {
  actor: 'user';
  type: 'todo';
  id: number;
  todo: ITodoListItem;
  deadline?: number | '';
}

export interface MemoryListChatItem extends MemoryListItemBase {
  actor: 'user';
  type: 'chat';
  id: number;
  chat: IChatListItem;
}

export interface MemoryListSerendipityItem extends MemoryListItemBase {
  actor: 'mind';
  type: 'serendipity';
  id: number;
  serendipity: SerendipityItem;
}

export interface MemoryListSerendipityList extends MemoryListItemBase {
  actor: 'mind';
  type: 'serendipityList';
  id: number;
  serendipityList: MemoryListSerendipityItem[];
}

export interface IFeaturedTalkInfo {
  id: string;
  featuredTitle?: string;
  featuredContent?: string;
  title?: string;
  cover?: string;
  shareCode?: string;
  avatars?: string[];
  instruction?: string;
  participantCount?: number;
  outputMode?: EOutputMode;
  createTimestamp?: number;
  creatorName?: string;
}

export interface IFeaturedSnapInfo {
  id: string;
  featuredTitle?: string;
  featuredContent?: string;
  image?: string;
  shareCode?: string;
  avatars?: string[];
  participantCount?: number;
  createTimestamp?: number;
  creatorName?: string;
}

export interface MemoryListTalkItem extends MemoryListItemBase {
  actor: 'mind';
  type: 'talk';
  id: number;
  talk: IFeaturedTalkInfo;
}

export interface MemoryListSnapItem extends MemoryListItemBase {
  actor: 'mind';
  type: 'snap';
  id: number;
  snap: IFeaturedSnapInfo;
}

export enum ELikeStatus {
  thumbsDown = 'thumbsDown',
  none = 'none',
  thumbsUp = 'thumbsUp'
}

export interface IGeneratedCTD {
  id: number;
  content: string;
  createTime: number;
  updateTime: number;
  noteDetail: INote_List; // 简化版
  likeStatus?: ELikeStatus;
  relatedNoteCount?: number;
  relatedNotesIdForUser?: number[];
}

export interface IUserLevel {
  id: number;
  level: number;
  topic?: string;
  onboarding?: string;
  finishGuide?: IFinishGuide;
}

export interface MemoryListUserLevelItem extends MemoryListItemBase {
  actor: 'mind';
  type: 'levelUp';
  id: number;
  userLevel: IUserLevel;
}

export function fetchMemoryList(data: IFetchMemoryListV2Req, signal?: AbortSignal) {
  return Request<CommonResponse<IFetchMemoryListRes>>({
    url: '/rest/os/note/memory/list/v3',
    method: 'post',
    data,
    signal
  });
}
export interface IhighLight {
  title: string | undefined;
  content: string | undefined;
  'attachments.content': string | undefined;
  'attachments.ogiTitle': string | undefined;
  insight: string | undefined;
  snippet: string | undefined;
}

export interface IFetchMemoryCountRes {
  [MemoryDataType.Todo]: number;
  [MemoryDataType.Doc]: number;
  [MemoryDataType.Audio]: number;
  // [MemoryDataType.Search]: number;
  [MemoryDataType.Image]: number;
  [MemoryDataType.Memo]: number;
  [MemoryDataType.Link_Doc]: number;
  [MemoryDataType.Chat]: number;
  [MemoryDataType.Answer]: number;
  [MemoryDataType.Page]: number;
  [MemoryDataType.Link]: number;
}

export const fetchMemoryCount = () => {
  return Request<CommonResponse<IFetchMemoryCountRes>>({
    url: '/rest/os/note/query/dataType/count',
    method: 'get'
  });
};

export interface IFetchMemorySourceCountRes {
  [EIntegrationType.APPLE]: number;
  [EIntegrationType.EVERNOTE]: number;
  [EIntegrationType.NOTION]: number;
  [EIntegrationType.X]: number;
  [EIntegrationType.MARKDOWN]: number;
}

export const fetchMemorySourceCount = () => {
  return Request<CommonResponse<IFetchMemorySourceCountRes>>({
    url: '/rest/os/note/query/source/count',
    method: 'get'
  });
};

export interface QuickProcessContentReq {
  content: string;
  type: 'longAudio' | 'shortAudio' | 'text';
  target: 'addNote' | 'addTodo';

  // 用于当前的文本场景
  createTodo?: boolean;

  // 用于短语音场景
  attachments?: string[];
  duration?: number; // 秒
  language?: string;

  // 备用字段
  openSelfNotice?: boolean;
  userTitle?: string;
}

interface IQuickProcessContentRes {
  noteId?: number;
  todos?: ITodoListItem[];
  searchId?: number;
  sessionId?: string;
}

export const addQuickContentNote = (data: QuickProcessContentReq) => {
  return Request<CommonResponse<IQuickProcessContentRes>>({
    url: '/rest/os/qp/content/add',
    method: 'post',
    data
  });
};

export const exportAllMemories = (data: { mindId: string }) => {
  return Request<CommonResponse<string>>({
    url: '/rest/os/note/export/all',
    method: 'post',
    data
  });
};

interface IUpdateSharePwd extends IOperateNoteReq {
  open: boolean;
  password?: string;
}
export const updateShareAccessPwd = (data: IUpdateSharePwd) => {
  return Request<CommonResponse<string>>({
    url: '/rest/os/note/share/put/pwd',
    method: 'post',
    data
  });
};

type ICheckShareTokenRes =
  | { owner: false; needPassword: boolean; isPublic: boolean }
  | { owner: true; noteId: number; needPassword: boolean; isPublic: boolean };
export const checkShareToken = (token: string) => {
  return Request<CommonResponse<ICheckShareTokenRes>>({
    url: `/rest/os/note/share/check/${token}`,
    method: 'post'
  });
};

interface ISaveFromOthersReq {
  sourceNote: string;
}

export const saveFromOthers = (data: ISaveFromOthersReq) => {
  return Request<CommonResponse<string>>({
    url: '/rest/os/note/save/others',
    method: 'post',
    data
  });
};

interface IImportMemoriesReq {
  source: 'evernote' | 'apple' | 'markdown';
  urls: string[];
}

interface IImportMemories {
  url: string;
  relatedNoteIds: string[];
  status: string;
}

export const importMemories = (data: IImportMemoriesReq) => {
  return Request<CommonResponse<IImportMemories[]>>({
    url: '/rest/os/note/import/cdnUrl',
    method: 'post',
    data
  });
};

export interface ITWitterInfo {
  twitterUid: string;
  profileImageUrl: string;
  name: string;
  screenName: string;
}

export const getTwitterInfo = (params: { twitterName: string }) => {
  return Request<CommonResponse<ITWitterInfo>>({
    url: 'rest/os/twitter/get/profile',
    method: 'get',
    params
  });
};

export const importTwitter = (data: { twitterUid: string }, signal?: AbortSignal) => {
  return Request<CommonResponse<any>>({
    url: 'rest/os/twitter/import/by/user',
    method: 'post',
    data,
    signal
  });
};

export enum EIntegrationType {
  NOTION = 'NOTION',
  EVERNOTE = 'EVERNOTE',
  APPLE = 'APPLE',
  X = 'X',
  MARKDOWN = 'MARKDOWN'
}

export interface IIntegrationUserInfo {
  id: string;
  userId?: string;
  status: 'VALID' | 'INVALID';
  accessToken?: string;
  accountName?: string;
  accountEmail?: string;
  accountAvatar?: string;
  platform: EIntegrationType;
  createTime?: string;
  updateTime?: string;
}

export interface IIntegrationStatusInfo {
  source: string;
  authInfo?: IIntegrationUserInfo;
  recordId?: string;
  status: 'done' | 'undone' | 'pending' | 'doing';
  totalNum?: number;
  finishNum?: number;
  successNum?: number;
  failNum?: number;
}

export const getIntegrationStatus = (platform?: EIntegrationType) => {
  return Request<CommonResponse<{ platformStatusList?: IIntegrationStatusInfo[] }>>({
    url: '/rest/os/note/import/platform/status',
    method: 'get',
    params: {
      platform
    }
  });
};

export const getAuthUrl = (platform: EIntegrationType) => {
  return Request<CommonResponse<string>>({
    url: '/rest/os/integration/get/authUrl',
    method: 'get',
    params: {
      platform
    }
  });
};

export const verifyAuthToken = (data: { platform: EIntegrationType; code: string }) => {
  return Request<CommonResponse<any>>({
    url: '/rest/os/integration/generate/accessToken',
    method: 'post',
    data
  });
};

export const pullIntegrationNotes = (data: { platform: EIntegrationType }) => {
  return Request<CommonResponse<{ noteNum: number; status: 'success' | 'fail'; errorMsg: string }>>(
    {
      url: '/rest/os/integration/pull/notes',
      method: 'post',
      data
    }
  );
};

export const cancelIntegration = (data: { id: string }) => {
  return Request<CommonResponse<any>>({
    url: '/rest/os/integration/cancel',
    method: 'post',
    data
  });
};

export function getTodoToday() {
  return Request<CommonResponse<ITodayTodoList>>({
    url: '/rest/os/task/todo/today',
    method: 'get'
  });
}

export function getAllTodo() {
  return Request<CommonResponse<ITodayTodoList>>({
    url: '/rest/os/task/todo/today/v2',
    method: 'get'
  });
}

export function getFeaturedList(data: { userId: string; pageNo?: number; pageSize?: number }) {
  const { userId, pageNo = 1, pageSize = 999 } = data;

  return Request<CommonResponse<IFetchMemoryListRes>>({
    url: '/rest/os/note/featured/list',
    method: 'post',
    data: {
      userId,
      pageNo,
      pageSize
    }
  });
}
